import React, { useCallback, useMemo, useState } from "react";
import { Button } from "antd";
import moment from "moment";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Cell,
} from "recharts";
import FilterBar from "components/filterBar";
import {
  KpiRow,
  KpiTile,
  CoverageNote,
  ChartCard,
  EmptyState,
  fmt,
} from "components/analytics";
import { useContractorManpower } from "hooks/analytics/analytics";
import { exportRowsToXlsx } from "helpers/exportXlsx";

/**
 * Contractor manpower.
 *
 * "Department" in this system is the contractor company supplying the worker, so
 * this is the report the site actually needs: who supplied how much labour, how
 * consistently, and what share of the site they accounted for.
 */

const BAR = "#1e5dfe";
const BAR_MUTED = "#c7d7ff";

const SORTS = {
  manDays: { label: "Man-days", get: (c) => c.manDays },
  people: { label: "People", get: (c) => c.people },
  avgDailyStrength: { label: "Avg daily strength", get: (c) => c.avgDailyStrength },
  daysPerPerson: { label: "Days per person", get: (c) => c.daysPerPerson },
};

const BarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const c = payload[0].payload;
  return (
    <div className="an-tip">
      <div className="an-tip-title">{c.contractor}</div>
      <div className="an-tip-row"><span>Man-days</span><span>{fmt(c.manDays)}</span></div>
      <div className="an-tip-row"><span>People</span><span>{fmt(c.people)}</span></div>
      <div className="an-tip-row"><span>Avg daily</span><span>{fmt(c.avgDailyStrength, 1)}</span></div>
      <div className="an-tip-row"><span>Share</span><span>{c.sharePercent ?? "—"}%</span></div>
    </div>
  );
};

const ContractorManpower = () => {
  const [filters, setFilters] = useState(null);
  const [sortKey, setSortKey] = useState("manDays");
  const { mutate: load, data: res, isLoading } = useContractorManpower();

  const onApply = useCallback(
    (nextFilters) => {
      setFilters(nextFilters);
      load(nextFilters);
    },
    [load]
  );

  const data = res?.data;
  const contractors = useMemo(() => {
    const list = [...(data?.contractors || [])];
    const getter = SORTS[sortKey].get;
    return list.sort((a, b) => (getter(b) ?? 0) - (getter(a) ?? 0));
  }, [data, sortKey]);

  const chartData = useMemo(() => contractors.slice(0, 12), [contractors]);

  /**
   * Days per person is the consistency signal: two firms can supply the same
   * man-days with very different reliability per worker.
   */
  const mostConsistent = useMemo(() => {
    const eligible = contractors.filter((c) => (c.people || 0) >= 5);
    if (!eligible.length) return null;
    return eligible.reduce((best, c) =>
      (c.daysPerPerson ?? 0) > (best.daysPerPerson ?? 0) ? c : best
    );
  }, [contractors]);

  const downloadExcel = () => {
    const rows = [
      ["Contractor", "People", "Man-days", "Days present", "Avg daily strength",
       "Days per person", "Share %", "Measured records", "Avg hours"],
    ];
    contractors.forEach((c) =>
      rows.push([
        `"${(c.contractor || "").replace(/"/g, '""')}"`,
        c.people, c.manDays, c.daysPresent, c.avgDailyStrength,
        c.daysPerPerson, c.sharePercent ?? "", c.measured, c.avgHours ?? "",
      ])
    );
    exportRowsToXlsx(rows, "Contractor_Manpower", "Contractor Manpower");
  };

  const hasData = contractors.length > 0;

  return (
    <div className="an-page">
      <FilterBar
        onApply={onApply}
        showCompare={false}
        title={
          <>
            <h1>Contractor manpower</h1>
            <p>Labour supplied per contractor</p>
          </>
        }
        actions={
          <Button type="primary" className="csv" disabled={!hasData} onClick={downloadExcel}>
            Download Excel
          </Button>
        }
      />

      <KpiRow>
        <KpiTile
          label="Total man-days"
          tone="primary"
          value={fmt(data?.totalManDays)}
          foot={
            data ? `${moment(data.from).format("DD MMM")} – ${moment(data.to).format("DD MMM YYYY")}` : ""
          }
        />
        <KpiTile label="Contractors" value={fmt(contractors.length)} foot="Supplying labour in range" />
        <KpiTile
          label="Largest supplier"
          value={contractors[0]?.contractor || "—"}
          foot={contractors[0] ? `${fmt(contractors[0].manDays)} man-days · ${contractors[0].sharePercent}%` : ""}
        />
        <KpiTile
          label="Most consistent"
          value={mostConsistent?.contractor || "—"}
          foot={
            mostConsistent
              ? `${fmt(mostConsistent.daysPerPerson, 1)} days per person (5+ workers)`
              : "Needs a firm with 5+ workers"
          }
        />
      </KpiRow>

      <CoverageNote coverage={data?.coverage} />

      <ChartCard
        title="Man-days by contractor"
        subtitle={chartData.length === 12 ? "Top 12 shown; the full list is in the table below." : undefined}
      >
        {hasData ? (
          <ResponsiveContainer width="100%" height={Math.max(260, chartData.length * 34)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 8 }}>
              <CartesianGrid stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} stroke="#eaecf0" />
              <YAxis
                type="category"
                dataKey="contractor"
                width={150}
                tick={{ fontSize: 11.5, fill: "#475467" }}
                stroke="#eaecf0"
              />
              <RTooltip content={<BarTooltip />} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="manDays" name="Man-days" radius={[0, 4, 4, 0]} barSize={18}>
                {chartData.map((entry, index) => (
                  <Cell key={entry.contractorId ?? index} fill={index === 0 ? BAR : BAR_MUTED} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState loading={isLoading} hasFilters={!!filters} />
        )}
      </ChartCard>

      <ChartCard
        title="Contractor detail"
        subtitle="Click a column heading to re-rank."
        actions={Object.entries(SORTS).map(([key, sort]) => (
          <Button
            key={key}
            size="small"
            type={sortKey === key ? "primary" : "default"}
            onClick={() => setSortKey(key)}
          >
            {sort.label}
          </Button>
        ))}
      >
        {hasData ? (
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>Contractor</th>
                  <th className="num">People</th>
                  <th className="num">Man-days</th>
                  <th className="num">Days present</th>
                  <th className="num">Avg daily</th>
                  <th className="num">Days / person</th>
                  <th>Share of site</th>
                  <th className="num">Avg hours</th>
                </tr>
              </thead>
              <tbody>
                {contractors.map((c) => (
                  <tr key={c.contractorId ?? c.contractor}>
                    <td>{c.contractor}</td>
                    <td className="num">{fmt(c.people)}</td>
                    <td className="num">{fmt(c.manDays)}</td>
                    <td className="num">{fmt(c.daysPresent)}</td>
                    <td className="num">{fmt(c.avgDailyStrength, 1)}</td>
                    <td className="num">{fmt(c.daysPerPerson, 1)}</td>
                    <td>
                      <span className="an-share">
                        <span className="an-share-bar">
                          <span style={{ width: `${Math.min(100, c.sharePercent || 0)}%` }} />
                        </span>
                        {c.sharePercent ?? "—"}%
                      </span>
                    </td>
                    <td className="num">{fmt(c.avgHours, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState loading={isLoading} hasFilters={!!filters} />
        )}
      </ChartCard>
    </div>
  );
};

export default ContractorManpower;
